/**
 * The ONE session resolver (§7, D1). Computed once per load, on every auth change, on a 5-minute timer
 * and when the tab becomes visible again. No component calls the auth API or reads a role table itself —
 * everything derives from `useSession()`. `loading` is a real state and is never read as signed out.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { setReporter } from './report';
import { capabilitiesFor, eventOnlyCapabilities, HOTEL_ACCOUNT_CAPABILITIES, NONE, type Capability, type Role } from './capabilities';

export type SessionContext =
  | { state: 'loading' }
  | { state: 'unauthenticated' }
  | { state: 'session-expired'; reason: 'expired' | 'role-changed' | 'profile-removed' | 'idle' }
  | { state: 'account-disabled' }
  | { state: 'no-membership'; userId: string; email: string }
  | { state: 'hotel-account'; userId: string; email: string; displayName: string; hotelIds: readonly string[]; capabilities: ReadonlySet<Capability> }
  | {
      state: 'authenticated-single-tenant';
      userId: string;
      email: string;
      displayName: string;
      orgId: string;
      orgName: string;
      role: Role;
      capabilities: ReadonlySet<Capability>;
      sandboxOrg: boolean;
      /** Opt-in idle sign-out (§7); null = off. */
      sessionTimeoutMinutes: number | null;
      /** The platform super admin (granted only in the database, never from the browser). */
      isPlatformAdmin: boolean;
      /** His own account, to come back to after opening someone else's; null for everyone else. */
      platformHome: { orgId: string; orgName: string } | null;
    };

interface Profile {
  id: string;
  principal_type: 'member' | 'hotel_account';
  org_id: string | null;
  org_role: Role | null;
  hotel_ids: string[];
  display_name: string | null;
  email: string | null;
  role_version: number | null;
  disabled_at: string | null;
}

interface SessionApi {
  session: SessionContext;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  /** The idle timer ran out: sign out into `session-expired` (never a setup screen), keeping the current URL. */
  expireIdle: () => Promise<void>;
  can: (cap: Capability) => boolean;
}

const Ctx = createContext<SessionApi | null>(null);
const ROLE_VERSION_KEY = 'rmp2.roleVersion';
const RECHECK_MS = 5 * 60 * 1000;

/**
 * Opening another account (or going back) changes the profile's role version on purpose. The platform page stores
 * the version the routine returned BEFORE it refreshes, so the role-version guard keeps the session (R6).
 */
export function storeRoleVersion(userId: string, version: number): void {
  sessionStorage.setItem(`${ROLE_VERSION_KEY}.${userId}`, String(Number(version) || 0));
}

interface PlatformRow { home_org_id: string | null; home_org_name: string | null }
/** One row for the platform super admin, nothing for anyone else. Any failure reads as "not a platform admin". */
async function platformMe(): Promise<PlatformRow | null> {
  try {
    const { data, error } = await supabase.rpc('platform_me');
    if (error) return null;
    const row = (Array.isArray(data) ? data[0] : data) as PlatformRow | null | undefined;
    return row && typeof row === 'object' ? row : null;
  } catch {
    return null;
  }
}

async function resolve(authSession: Session | null): Promise<SessionContext> {
  if (!authSession) return { state: 'unauthenticated' };
  const userId = authSession.user.id;
  const email = authSession.user.email ?? '';
  const { data: profile, error } = await supabase
    .from('user_profiles')
    .select('id, principal_type, org_id, org_role, hotel_ids, display_name, email, role_version, disabled_at')
    .eq('id', userId)
    .maybeSingle<Profile>();
  if (error) {
    if (error.code === 'PGRST301' || /JWT/i.test(error.message)) return { state: 'session-expired', reason: 'expired' };
    throw error;
  }
  if (!profile) return { state: 'no-membership', userId, email };
  if (profile.disabled_at) return { state: 'account-disabled' };
  // Role-version guard (§7): a mismatch in EITHER direction ends the session; null/NaN coerced to 0.
  const seen = Number(sessionStorage.getItem(`${ROLE_VERSION_KEY}.${userId}`) ?? 'NaN');
  const current = Number(profile.role_version) || 0;
  if (Number.isFinite(seen) && seen !== current) {
    sessionStorage.removeItem(`${ROLE_VERSION_KEY}.${userId}`);
    return { state: 'session-expired', reason: 'role-changed' };
  }
  sessionStorage.setItem(`${ROLE_VERSION_KEY}.${userId}`, String(current));
  const displayName = profile.display_name || email;
  if (profile.principal_type === 'hotel_account') {
    return { state: 'hotel-account', userId, email, displayName, hotelIds: profile.hotel_ids ?? [], capabilities: HOTEL_ACCOUNT_CAPABILITIES };
  }
  if (!profile.org_id || !profile.org_role) return { state: 'no-membership', userId, email };
  const [{ data: org }, platform] = await Promise.all([
    supabase.from('organizations').select('name, is_sandbox_org, session_timeout_minutes').eq('id', profile.org_id).maybeSingle(),
    platformMe(),
  ]);
  // the organization can't be read: a turned-off account says so, rather than an empty workspace
  if (!org) {
    const { data: off } = await supabase.rpc('my_org_disabled');
    if (off === true) return { state: 'account-disabled' };
  }
  let capabilities = capabilitiesFor(profile.org_role);
  if (profile.org_role === 'event_only') {
    // their own grants only; a failed read gives nothing (never a fallback that escalates)
    const { data: grants, error: gErr } = await supabase.from('event_members').select('role').eq('user_id', userId);
    capabilities = eventOnlyCapabilities(gErr ? [] : (grants ?? []).map((g) => g.role as Role));
  }
  // before he first opens another account his home is not recorded yet: it is the account he is in
  const home = platform ? { orgId: platform.home_org_id ?? profile.org_id, orgName: platform.home_org_name ?? org?.name ?? 'Your organization' } : null;
  if (home) capabilities = new Set<Capability>([...capabilities, 'platform.manage']);
  return {
    state: 'authenticated-single-tenant',
    userId,
    email,
    displayName,
    orgId: profile.org_id,
    orgName: org?.name ?? 'Your organization',
    role: profile.org_role,
    capabilities,
    sandboxOrg: Boolean(org?.is_sandbox_org),
    sessionTimeoutMinutes: Number(org?.session_timeout_minutes) > 0 ? Number(org?.session_timeout_minutes) : null,
    isPlatformAdmin: Boolean(home),
    platformHome: home,
  };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionContext>({ state: 'loading' });
  const hadCredential = useRef(false);
  const idled = useRef(false);

  const run = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    try {
      const next = await resolve(data.session);
      // A check that was already in flight when the idle sign-out happened must not overwrite why it happened.
      if (idled.current) { setSession({ state: 'session-expired', reason: 'idle' }); return; }
      // A credential that disappears (refresh failure, server-side revocation) is "expired", not "never signed in".
      if (next.state === 'unauthenticated' && hadCredential.current) setSession({ state: 'session-expired', reason: 'expired' });
      else setSession(next);
      if (data.session) hadCredential.current = true;
    } catch {
      setSession({ state: 'session-expired', reason: idled.current ? 'idle' : 'expired' });
    }
  }, []);

  useEffect(() => {
    void run();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        hadCredential.current = false;
        setSession(idled.current ? { state: 'session-expired', reason: 'idle' } : { state: 'unauthenticated' });
        return;
      }
      if (event === 'SIGNED_IN') idled.current = false;
      // Deferred: calling back into the auth client from inside its own callback waits on the lock the callback
      // is still holding, which left the app on "Checking your session…" (supabase-js onAuthStateChange caveat).
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') window.setTimeout(() => void run(), 0);
    });
    const timer = window.setInterval(() => void run(), RECHECK_MS);
    const onVisible = () => document.visibilityState === 'visible' && void run();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      sub.subscription.unsubscribe();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [run]);

  // Report sends as the signed-in person when there is one, and as a guest otherwise
  useEffect(() => {
    setReporter(session.state === 'authenticated-single-tenant' || session.state === 'hotel-account' || session.state === 'no-membership');
  }, [session.state]);

  const api = useMemo<SessionApi>(() => {
    const caps = 'capabilities' in session ? session.capabilities : NONE;
    return {
      session,
      refresh: run,
      signOut: async () => {
        hadCredential.current = false;
        idled.current = false;
        await supabase.auth.signOut();
        setSession({ state: 'unauthenticated' });
      },
      expireIdle: async () => {
        idled.current = true;
        hadCredential.current = false;
        // this device only: one idle tab must not sign the person out of their other devices
        await supabase.auth.signOut({ scope: 'local' });
        setSession({ state: 'session-expired', reason: 'idle' });
      },
      can: (cap) => caps.has(cap),
    };
  }, [session, run]);

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useSession(): SessionApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** Exhaustiveness helper for switches over SessionContext['state']. */
export function assertNever(x: never): never {
  throw new Error(`Unhandled session state: ${JSON.stringify(x)}`);
}
